import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { DateTime } from 'luxon'
import { MessagesState, RootState } from 'types/state'
import _, { uniqBy } from 'lodash'
import { ChatMessage, FeedbackEnum, MessageSourceEnum, MessageTypeEnum } from 'types/chat-message'
import { Chat, ChatResponseModeEnum, ChatRoleEnum } from 'types/chat'
import { findParticipantsByRole } from 'utils/chats'
import { TicketStatusEnum } from 'types/ticket'

const initialState: MessagesState = {
  openChatLoading: false,
  chatId: undefined,
  chats: {}, // oggetto per manipolarlo meglio con ws, es sta scrivendo etc ...
  messages: [],
  botTyping: false,
  userTyping: true,
  userTypingPlaceholder: 'typeMessage',
  chatInitialized: false,
}
export const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setChatId: (state: MessagesState, action: PayloadAction<MessagesState['chatId']>) => {
      state.chatId = action.payload
    },
    addChat: (state: MessagesState, { payload: newChat }: PayloadAction<Chat>) => {
      // add chat to first position
      // state.chats[newChat._id] = newChat
      state.chats = { [newChat._id]: newChat, ...state.chats }
    },
    addChats: (state: MessagesState, { payload: newChats }: PayloadAction<Chat[]>) => {
      state.chats = {}
      newChats.forEach((newChat) => {
        state.chats[newChat._id] = newChat
      })
    },
    updateChatLastMessage: (state: MessagesState) => {
      const chatId = state.chatId
      const lastChatMessage = _.last(state.messages)
      if (chatId && lastChatMessage) {
        state.chats[chatId].lastMessage = lastChatMessage
        state.chats[chatId].updatedAt = String(DateTime.now().toISO())
      }
    },
    updateCurrentChat: (state: MessagesState, action: PayloadAction<Partial<Chat>>) => {
      const chatId = state.chatId
      if (chatId) {
        state.chats[chatId] = {
          ...state.chats[chatId],
          ...action.payload,
        }
      }
    },
    editChat: (state: MessagesState, { payload: editedChat }: PayloadAction<Chat>) => {
      state.chats[editedChat._id] = editedChat
    },
    updateChat: (state: MessagesState, { payload: editedChat }: PayloadAction<Partial<Chat>>) => {
      if (editedChat._id) {
        if (state.chats[editedChat._id]) {
          state.chats[editedChat._id].lastMessage = editedChat.lastMessage
        }
      }
    },
    removeChat: (state: MessagesState, { payload: chatId }: PayloadAction<string>) => {
      delete state.chats[chatId]
    },
    setChatInitialized: (state: MessagesState, action: PayloadAction<MessagesState['chatInitialized']>) => {
      state.chatInitialized = action.payload
    },
    setMessages: (state: MessagesState, action: PayloadAction<MessagesState['messages']>) => {
      state.messages = action.payload
    },
    setOpenChatLoading: (state: MessagesState, action: PayloadAction<MessagesState['openChatLoading']>) => {
      state.openChatLoading = action.payload
    },
    updateMessage: (
      state: MessagesState,
      action: PayloadAction<{ messageId: string; messageData: Partial<ChatMessage> }>,
    ) => {
      const messageId = action.payload.messageId
      const messageData = action.payload.messageData

      state.messages.map((message) => {
        if (message._id === messageId) {
          message = { ...message, ...messageData }
        }
        return message
      })
    },
    addNewMessage: (state: MessagesState, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload)
    },
    addQuestionMessage: (
      state: MessagesState,
      action: PayloadAction<{ message: string; _id: string; senderId: string; chatSenderRole: ChatRoleEnum }>,
    ) => {
      if (!state.chatId) {
        return
      }
      state.messages.push({
        message: action.payload.message,
        senderId: action.payload.senderId,
        chatSenderRole: action.payload.chatSenderRole,
        type: MessageTypeEnum.TEXT,
        createdAt: String(DateTime.now().toISO()),
        timestamp: DateTime.now().toMillis(),
        _id: action.payload._id,
        chatId: state.chatId,
        chatResponseMode: ChatResponseModeEnum.BOT,
      })
    },
    addMessage: (state: MessagesState, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload)
    },
    startBotResponse: (state: MessagesState) => {
      if (!state.chatId) {
        return
      }
      state.botTyping = true
      state.userTyping = false
      state.userTypingPlaceholder = 'waitBotResponse'
    },
    streamBotResponse: (
      state: MessagesState,
      { payload }: PayloadAction<{ botMessageId: string; partialResponse: string; senderId: string }>,
    ) => {
      state.botTyping = false
      if (!state.chatId) {
        return
      }
      const { botMessageId, partialResponse } = payload
      const index = state.messages.findIndex((message) => message._id === botMessageId)
      if (index > -1) {
        state.messages[index].message = partialResponse
      } else {
        state.messages.push({
          message: partialResponse,
          senderId: payload.senderId,
          chatSenderRole: ChatRoleEnum.BOT,
          type: MessageTypeEnum.TEXT,
          createdAt: String(DateTime.now().toISO()),
          timestamp: DateTime.now().toMillis(),
          _id: botMessageId,
          chatResponseMode: ChatResponseModeEnum.BOT,
          chatId: state.chatId,
          messageSource: MessageSourceEnum.localBotResponse,
        })
      }
    },
    endBotResponse: (state: MessagesState) => {
      state.botTyping = false
      state.userTyping = true
      state.userTypingPlaceholder = 'typeMessage'
    },
    addMessages: (state: MessagesState, action: PayloadAction<ChatMessage[]>) => {
      state.messages = uniqBy([...state.messages, ...action.payload], 'id')
    },
    clearMessages: (state: MessagesState) => {
      state.messages = []
    },
    setMessageFeedback: (
      state: MessagesState,
      action: PayloadAction<{ messageId: string; feedback: FeedbackEnum }>,
    ) => {
      const { messageId, feedback } = action.payload
      const index = state.messages.findIndex((message) => message.id === messageId)
      if (index > -1) {
        state.messages[index].feedback = feedback
      }
    },
    resetMessageState: () => {
      return initialState
    },
    removeAllMessages: (state: MessagesState) => {
      state.messages = []
    },
    toggleUserTyping: (state: MessagesState, action: PayloadAction<boolean>) => {
      state.userTyping = action.payload
    },
    toggleBotTyping: (state: MessagesState, action: PayloadAction<boolean>) => {
      state.botTyping = action.payload
      state.userTypingPlaceholder = action.payload ? 'waitBotResponse' : 'typeMessage'
    },
    setBotTyping: (state: MessagesState, action: PayloadAction<boolean>) => {
      state.botTyping = action.payload
    },
    setUserTyping: (state: MessagesState, action: PayloadAction<boolean>) => {
      state.userTyping = action.payload
    },
    setChats: (state: MessagesState, action: PayloadAction<MessagesState['chats']>) => {
      state.chats = action.payload
    },
    setChat: (state: MessagesState, action: PayloadAction<Chat>) => {
      state.chats[action.payload._id] = action.payload
    },
    setUserTypingPlaceholder: (state: MessagesState, action: PayloadAction<'waitBotResponse' | 'typeMessage'>) => {
      state.userTypingPlaceholder = action.payload
    },
  },
})

export const {
  addQuestionMessage,
  toggleBotTyping,
  toggleUserTyping,
  streamBotResponse,
  endBotResponse,
  setChatId,
  setMessageFeedback,
  clearMessages,
  setChatInitialized,
  addChat,
  editChat,
  removeChat,
  setBotTyping,
  setMessages,
  updateChatLastMessage,
  setUserTyping,
  addChats,
  addMessage,
  setChats,
  updateCurrentChat,
  updateChat,
  setChat,
  setUserTypingPlaceholder,
} = messagesSlice.actions

export const selectChatId = (rootState: RootState) => rootState.messageState.chatId
export const selectOpenChatLoading = (rootState: RootState) => rootState.messageState.openChatLoading
export const selectMessages = (rootState: RootState) => rootState.messageState.messages
export const selectHasMessages = (rootState: RootState) => rootState.messageState?.messages?.length > 0
export const selectChatInitialized = (rootState: RootState) => rootState.messageState?.chatInitialized

export const messagesActions = {
  addChat,
  editChat,
  removeChat,
  clearMessages,
  setChatId,
  setMessages,
  updateChatLastMessage,
  setUserTypingPlaceholder,
  setBotTyping,
  setChats,
  setUserTyping,
  endBotResponse,
  addChats,
  addMessage,
  updateCurrentChat,
  updateChat,
  setChat,
}

export const messagesSelectors = {
  chatId: selectChatId,
  chats: (state: RootState) => state.messageState.chats,
  guestChats: (state: RootState) => {
    const chatsArray = _.values(state.messageState.chats)
    const isLogged = !!state.settingsState.token
    const botChats = chatsArray.filter((chat) => chat.responseMode === ChatResponseModeEnum.BOT)
    return _.filter(botChats, (chat) => {
      const guest = findParticipantsByRole(chat, isLogged ? ChatRoleEnum.USER : ChatRoleEnum.GUEST)
      return guest?.userId === chat.userId
    })
  },
  currentChat: (state: RootState) => {
    const chatId = selectChatId(state)
    if (!chatId) {
      return undefined
    }
    return state.messageState.chats[chatId]
  },
  currentChatBotId: (state: RootState) => {
    const chatId = selectChatId(state)
    if (!chatId) {
      return undefined
    }
    return findParticipantsByRole(state.messageState.chats[chatId], ChatRoleEnum.BOT)?.userId
  },
  currentChatTicket: (state: RootState) => {
    const chatId = selectChatId(state)
    if (!chatId) {
      return undefined
    }
    return state.messageState.chats[chatId].ticket
  },
  currentChatIsInOperatorMode: (state: RootState) => {
    const chatId = selectChatId(state)
    if (!chatId) {
      return false
    }
    return state.messageState.chats[chatId].responseMode === ChatResponseModeEnum.OPERATOR
  },
  currentChatParticipants: (state: RootState) => {
    const chatId = selectChatId(state)
    if (!chatId) {
      return []
    }
    return state.messageState.chats[chatId].participants || []
  },
  disableFieldsTicketClosed: (state: RootState) => {
    const ticket = messagesSelectors.currentChatTicket(state)
    if (!ticket) {
      return false
    }
    return !(ticket.status === TicketStatusEnum.NEW || ticket.status === TicketStatusEnum.IN_PROGRESS)
  },
  userTyping: (state: RootState) => state.messageState.userTyping,
  userTypingPlaceholder: (state: RootState) => state.messageState.userTypingPlaceholder,
  selectOpenChatLoading,
  selectMessages,
  selectHasMessages,
  selectChatInitialized,
}

export default messagesSlice.reducer
